import type {
  AccessGraphEntry,
  AccessGraphFolderRef,
  AccessGraphShareTargetRef,
  AccessGraphShareableRef,
  AccessPath,
  AccessStep,
  ContainedInStep,
  GrantStep,
} from './access-graph.model';
import type {
  BdSharePermission,
} from './shared.model';
import {
  BD_SHARE_PERMISSION_SHORT_NAME
} from './shared.model';

/** Ascending, so a permission list always reads in the same order. */
const PERMISSION_ORDER: BdSharePermission[] = ['READ', 'UPDATE', 'DELETE'];

/** German labels for the group membership types the backend emits. */
const MEMBERSHIP_TYPE_NAME: Record<string, string> = {
  ADMIN: 'Administrator',
  CONTRIBUTOR: 'Mitwirkender',
  READER: 'Leser',
};

/**
 * One piece of a path explanation. `emphasis` marks the parts that name
 * something — a folder, a person, a permission — so the template can set them
 * apart without the domain lib knowing any markup.
 */
export interface AccessPathSegment {
  text: string;
  emphasis: boolean;
  /**
   * Set only on the segment that names a `CONTAINED_IN` folder, and only when
   * the step was not redacted — a folder the caller has no READ on carries no
   * id worth following. Pure data: the domain lib states *which* folder the
   * segment names, the UI decides whether that becomes a link (BDA-5990).
   */
  folder?: AccessGraphFolderRef;
}

/**
 * The label shown when a share target, folder or company no longer resolves to
 * a name. Falling back to the id rather than hiding the row is deliberate:
 * access via a deleted target is a fact worth surfacing (concept 5).
 */
export function shareTargetLabel(
  target: AccessGraphShareTargetRef | null,
): string {
  if (!target) {
    return 'jemand, den Sie nicht sehen können';
  }
  return target.name?.trim() || target.id;
}

/**
 * The label for a `CONTAINED_IN` step's folder. A redacted step keeps its place
 * on the path — dropping it would make the path unexplainable — but names the
 * folder only as one the caller cannot see (concept 4).
 */
export function folderLabel(folder: AccessGraphFolderRef | null): string {
  if (!folder) {
    return 'einem Ordner, den Sie nicht sehen können';
  }
  return folder.name?.trim() || folder.id;
}

/** `[READ, DELETE]` → `'Anzeigen, Löschen'`, always in ascending order. */
export function permissionsLabel(permissions: BdSharePermission[]): string {
  const ordered = PERMISSION_ORDER.filter((p) => permissions.includes(p));
  if (ordered.length === 0) {
    return 'kein Zugriff';
  }
  return ordered.map((p) => BD_SHARE_PERMISSION_SHORT_NAME[p]).join(', ');
}

/** `'2026-08-27T10:15:30+02:00'` → `'27.08.2026'`; the raw value if unparseable. */
export function formatAccessDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
}

/** Whether the path is anchored to no company and so applies in every context. */
export function isContextFree(path: AccessPath): boolean {
  return path.companyContext === null;
}

/**
 * The paths that apply in the requested company context. `activeInContext` is
 * `null` when no context was requested, in which case every path counts.
 */
export function activePathsOf(entry: AccessGraphEntry): AccessPath[] {
  return entry.paths.filter((path) => path.activeInContext !== false);
}

/**
 * The permission set the entry actually confers in the requested company
 * context, derived from the active paths only.
 *
 * `entry.permissions` is the union over **all** paths regardless of context, so
 * an entry whose every path is inactive still reports its full set there.
 * Rendering that header over dimmed paths reads as a contradiction; this is the
 * client-side resolution the ticket asks for (concept 3).
 */
export function activePermissionsOf(
  entry: AccessGraphEntry,
): BdSharePermission[] {
  const active = activePathsOf(entry);
  const granted = new Set(active.flatMap((path) => path.permissions));
  return PERMISSION_ORDER.filter((p) => granted.has(p));
}

/**
 * Whether the entry confers nothing in the requested company context — every
 * path is inactive. Always `false` when no context was requested.
 */
export function isInactiveInContext(entry: AccessGraphEntry): boolean {
  return entry.paths.length > 0 && activePathsOf(entry).length === 0;
}

/** Whether any of the entry's paths follows the target into every company context. */
export function hasContextFreeAccess(entry: AccessGraphEntry): boolean {
  return entry.paths.some(isContextFree);
}

/** `'folder'` when the access is inherited from a containing folder. */
export function accessOriginOf(path: AccessPath): 'folder' | 'direct' {
  return path.steps.some(isContainedIn) ? 'folder' : 'direct';
}

/**
 * What a revoke call must reference for this path, or `null` when the path is
 * not revocable at all (folder ownership — only a move changes that).
 *
 * This is `step.on`, never the dialog's own shareable: a grant on a containing
 * folder is revoked *on that folder*, and requires DELETE on it rather than on
 * the shareable the dialog is about.
 */
export function revocationTargetOf(
  path: AccessPath,
): AccessGraphShareableRef | null {
  return findGrant(path)?.on ?? null;
}

/**
 * Whether revoking this path would act on something other than the shareable
 * the dialog is about — a containing folder. Such a path is visible to anyone
 * with UPDATE on the shareable but actionable only with DELETE on that folder,
 * so it renders read-only as "managed in folder X".
 */
export function isManagedElsewhere(
  path: AccessPath,
  shareable: AccessGraphShareableRef,
): boolean {
  const target = revocationTargetOf(path);
  if (!target) {
    return true;
  }
  return target.id !== shareable.id || target.type !== shareable.type;
}

/**
 * The path as a sentence, read from the shareable outwards: what it grants,
 * through which folders, and what finally confers it.
 *
 * Three terminal shapes, per concept 2 and the `expand=users` steps:
 * - a `GRANT`, which names a grantor and a date (or says the grantor is hidden);
 * - `FOLDER_OWNER`, which names neither and instead points at `anchor`, the
 *   entry the path belongs to;
 * - an optional `GROUP_MEMBER`/`COMPANY_MEMBER` suffix explaining how an
 *   expanded user reaches their anchor's access.
 */
export function describeAccessPath(
  path: AccessPath,
  anchor: AccessGraphShareTargetRef,
): AccessPathSegment[] {
  const segments: AccessPathSegment[] = [
    { text: permissionsLabel(path.permissions), emphasis: true },
  ];
  const folders = path.steps.filter(isContainedIn);

  folders.forEach((step, index) => {
    segments.push({
      text: index === 0 ? ' — das Element liegt in ' : ', der in ',
      emphasis: false,
    });
    segments.push({
      text: folderLabel(step.folder),
      emphasis: true,
      ...(step.folder ? { folder: step.folder } : {}),
    });
  });

  const grant = findGrant(path);
  if (grant) {
    segments.push(...describeGrant(grant, folders.length > 0));
  } else if (path.steps.some(isFolderOwner)) {
    // ", der X gehört" needs the folder clause in front of it as its
    // antecedent. When the shareable *is* the owned folder there is no
    // CONTAINED_IN step and therefore no clause, and the relative pronoun
    // dangled ("Anzeigen, Bearbeiten, Löschen, der Anna gehört.").
    if (folders.length > 0) {
      segments.push({ text: ', der ', emphasis: false });
      segments.push({ text: folderOwnerLabel(path, anchor), emphasis: true });
      segments.push({ text: ' gehört.', emphasis: false });
    } else {
      segments.push({ text: ' — dieses Element gehört ', emphasis: false });
      segments.push({ text: folderOwnerLabel(path, anchor), emphasis: true });
      segments.push({ text: '.', emphasis: false });
    }
  } else {
    segments.push({ text: '.', emphasis: false });
  }

  segments.push(...describeMembership(path.steps));
  return segments;
}

/** The plain-text rendering of {@link describeAccessPath}, for tooltips and tests. */
export function accessPathText(segments: AccessPathSegment[]): string {
  return segments.map((s) => s.text).join('');
}

/**
 * Who owns the folder a `FOLDER_OWNER` path ends on.
 *
 * Usually the anchor itself. But under `expand=users` the anchor is the
 * *member*, while the folder belongs to the group or company they reach it
 * through — naming the member there would state something false ("the folder
 * belongs to Bea", when it belongs to her group).
 */
function folderOwnerLabel(
  path: AccessPath,
  anchor: AccessGraphShareTargetRef,
): string {
  for (const step of path.steps) {
    if (step.kind === 'GROUP_MEMBER') {
      return shareTargetLabel(step.group);
    }
    if (step.kind === 'COMPANY_MEMBER') {
      return step.company.name?.trim() || step.company.id;
    }
  }
  return shareTargetLabel(anchor);
}

function describeGrant(
  grant: GrantStep,
  onFolder: boolean,
): AccessPathSegment[] {
  const segments: AccessPathSegment[] = [];
  const date = formatAccessDate(grant.createdDate);
  const permission = BD_SHARE_PERMISSION_SHORT_NAME[grant.permission];

  if (grant.grantor) {
    segments.push({
      text: onFolder ? '; auf diesen Ordner hat ' : ' — ',
      emphasis: false,
    });
    segments.push({ text: shareTargetLabel(grant.grantor), emphasis: true });
    // The folder clause already carries the verb ("auf diesen Ordner hat X …"),
    // so only the direct-grant clause needs it after the name.
    segments.push({
      text: onFolder ? ` am ${date} ` : ` hat am ${date} `,
      emphasis: false,
    });
    segments.push({ text: permission, emphasis: true });
    segments.push({ text: ' freigegeben.', emphasis: false });
    return segments;
  }

  // Redacted grantor: the grant itself stays visible, only who made it is
  // withheld. Passive voice keeps the sentence readable without a name.
  segments.push({
    text: onFolder ? '; auf diesen Ordner wurde am ' : ' — am ',
    emphasis: false,
  });
  segments.push({ text: `${date} `, emphasis: false });
  segments.push({ text: permission, emphasis: true });
  segments.push({
    text: ' freigegeben, von jemandem, den Sie nicht sehen können.',
    emphasis: false,
  });
  return segments;
}

function describeMembership(steps: AccessStep[]): AccessPathSegment[] {
  const segments: AccessPathSegment[] = [];
  for (const step of steps) {
    if (step.kind === 'GROUP_MEMBER') {
      const role =
        MEMBERSHIP_TYPE_NAME[step.membershipType] ?? step.membershipType;
      segments.push({ text: ' Zugriff über die Gruppe ', emphasis: false });
      segments.push({ text: shareTargetLabel(step.group), emphasis: true });
      segments.push({ text: ` (${role}).`, emphasis: false });
    } else if (step.kind === 'COMPANY_MEMBER') {
      segments.push({ text: ' Zugriff über das Unternehmen ', emphasis: false });
      segments.push({
        text: step.company.name?.trim() || step.company.id,
        emphasis: true,
      });
      segments.push({ text: '.', emphasis: false });
    }
  }
  return segments;
}

function findGrant(path: AccessPath): GrantStep | undefined {
  return path.steps.find(isGrant);
}

function isGrant(step: AccessStep): step is GrantStep {
  return step.kind === 'GRANT';
}

function isContainedIn(step: AccessStep): step is ContainedInStep {
  return step.kind === 'CONTAINED_IN';
}

function isFolderOwner(step: AccessStep): boolean {
  return step.kind === 'FOLDER_OWNER';
}
